import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useAuth } from '../App';
import { API, getImageUrl } from '../utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building2, Globe, MapPin, Calendar, Briefcase, 
  Users, Star, MessageCircle, ExternalLink 
} from 'lucide-react';

const BusinessProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Support slug format: "BusinessName_45b25a549684" or plain "user_45b25a549684"
  const resolvedUserId = userId.includes('_') && !userId.startsWith('user_')
    ? `user_${userId.split('_').pop()}`
    : userId;

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/business/${resolvedUserId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Business profile not found');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [resolvedUserId, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleMessage = () => {
    if (!user) {
      toast.error('Please log in to message');
      navigate('/login');
      return;
    }
    navigate('/messages', { state: { partnerId: userId } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-pulse text-gray-400 text-sm">Loading profile...</div>
      </div>
    );
  }

  if (!profile) return null;

  const memberSince = profile.member_since ? new Date(profile.member_since).getFullYear() : 'N/A';

  return (
    <div className="p-6 md:p-10 overflow-auto">
      {/* Profile Header */}
      <div className="mb-8 pb-8 border-b border-gray-100">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="w-24 h-24 rounded-2xl bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {profile.user_picture ? (
                <img src={getImageUrl(profile.user_picture)} alt={profile.user_name} className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-12 h-12 text-gray-300" />
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold tracking-tight">
                  {profile.company_name || profile.user_name}
                </h1>
              </div>
              
              {profile.industry && (
                <p className="text-lg text-gray-600 mb-3">{profile.industry}</p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.location}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Member since {memberSince}
                </div>
                {profile.website && (
                  <a 
                    href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-black hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{profile.total_projects_posted}</p>
                  <p className="text-xs text-gray-500">Projects Posted</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{profile.total_hires}</p>
                  <p className="text-xs text-gray-500">Creatives Hired</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            {user && user.user_id !== userId && (
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={handleMessage}
                  className="rounded-full px-8 h-12 bg-black text-white hover:bg-gray-900"
                  data-testid="message-business-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>
            )}
        </div>
      </div>

      {/* Content */}
      <div className="py-12 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* About */}
            <div className="md:col-span-2 space-y-8">
              {profile.description && (
                <section className="bg-white rounded-xl p-6 border border-gray-100">
                  <h2 className="text-xl font-bold mb-4">About</h2>
                  <p className="text-gray-600 leading-relaxed">{profile.description}</p>
                </section>
              )}

              {/* Reviews */}
              <section className="bg-white rounded-xl p-6 border border-gray-100">
                <h2 className="text-xl font-bold mb-4">Reviews from Creatives</h2>
                {profile.reviews && profile.reviews.length > 0 ? (
                  <div className="space-y-4">
                    {profile.reviews.map((review) => (
                      <div key={review.review_id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-gray-500">by {review.creative_name}</span>
                        </div>
                        <p className="text-gray-600">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No reviews yet</p>
                )}
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold mb-4">Business Info</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Contact Name</span>
                    <span className="font-medium">{profile.user_name}</span>
                  </div>
                  {profile.industry && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Industry</span>
                      <span className="font-medium">{profile.industry}</span>
                    </div>
                  )}
                  {profile.location && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Location</span>
                      <span className="font-medium">{profile.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {profile.completed_projects > 0 && (
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 mb-1">Verified Business</p>
                      <p className="text-sm text-amber-700">
                        This business has successfully completed {profile.completed_projects} project{profile.completed_projects > 1 ? 's' : ''} through FrameFinder.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;
